import { Icon } from "../icons/Icon"
import { ColorSamplePurpose, ColorSampleResult } from "@/core/color-sampler"
import { useAtom, useAtomValue } from "jotai"
import { colorPickerSessionAtom } from "@/store/color-picker-state"
import { getColorPipetteCursor } from "../canvas-controls/canvas-controls-utils"
import clsx from "clsx"
import {
	activeEditingToolPanelAtom,
	showingHealingPanelAtom,
	viewportCanvasSettingsAtom,
} from "@/store/ui-views-state"
import { useEditor } from "@/utils/useEditor"
import { maskOverlaySettingsAtom } from "@/store/editor/mask-overlays"

export interface ColorPickerPipetteProps {
	purpose: ColorSamplePurpose
	className?: string
	isForMask?: boolean
	onSelected: (result: ColorSampleResult) => boolean
}

export const ColorPickerPipette = (props: ColorPickerPipetteProps) => {
	const editor = useEditor()
	const [colorPickerSession, setColorPickerSession] = useAtom(colorPickerSessionAtom)
	const [activeToolPanel, setActiveToolPanel] = useAtom(activeEditingToolPanelAtom)
	const isHealingActive = useAtomValue(showingHealingPanelAtom)
	const [viewportCanvasSettings, setViewportCanvasSettings] = useAtom(viewportCanvasSettingsAtom)
	const [overlaySettings, setOverlaySettings] = useAtom(maskOverlaySettingsAtom)

	if (isHealingActive) {
		// don't do anything if healing is active
		return null
	}

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()

		if (colorPickerSession?.purpose === props.purpose) {
			setViewportCanvasSettings({
				...viewportCanvasSettings,
				isZoomableWithZ: true,
				isZoomableWithSpace: true,
				isZoomableWithMouse: true,
			})

			setColorPickerSession(null)

			editor.stopSamplingColor()
		} else {
			if (!props.isForMask && activeToolPanel !== "edit") {
				setActiveToolPanel("edit")
			}
			// hide overlay if it's shown
			if (props.isForMask && overlaySettings.showOverlay === true) {
				setOverlaySettings({
					...overlaySettings,
					showOverlay: false,
				})
			}
			setViewportCanvasSettings({
				...viewportCanvasSettings,
				isZoomableWithMouse: false,
				isZoomableWithZ: false,
				isZoomableWithSpace: false,
			})

			setColorPickerSession({
				purpose: props.purpose,
				handler: (result) => {
					const isFinished = props.onSelected(result)

					if (isFinished) {
						setColorPickerSession(null)
						setViewportCanvasSettings({
							...viewportCanvasSettings,
							isZoomableWithMouse: true,
							isZoomableWithZ: true,
							isZoomableWithSpace: true,
						})
					}

					return isFinished
				},
			})

			editor.startSamplingColor(props.purpose)
		}
	}

	return (
		<div
			style={{
				cursor:
					props.purpose === colorPickerSession?.purpose
						? getColorPipetteCursor()
						: undefined,
			}}
			data-colorpicker="true"
			onClick={handleClick}
			className={clsx(
				"flex size-5 items-center justify-center rounded-full border border-base-500 bg-base-700 text-base-50 hover:bg-base-600",
				props.className,
			)}
		>
			{props.purpose !== colorPickerSession?.purpose && (
				<Icon name="pipette" className="size-4 text-current" />
			)}
		</div>
	)
}
